/**
 * Freigabe durch die Marke und Berichtspaket (0073): Stände des Freigabe-Links, Hinweis zu den Korrekturschleifen (für die
 * Marke nie mit Betrag), Kennzahlen je Beitrag (von Hand, mit Instagram-Werten als Ergänzung), Summen und der Bericht als PDF.
 * Die Daten kommen aus public.brand_review_open / brand_report_open (bzw. den Vorschauen fürs Team); diese Funktionen
 * rechnen und beschriften nur. Reine Funktionen, getestet in packages/domain/test/brand-report.test.ts.
 */
import { addDaysIso } from "./contract-term.js";
import { renderContractPdf, type ContractPdfImage } from "./contract-pdf.js";
import type { Language } from "./types.js";

// ── Freigabe-Link ─────────────────────────────────────────────────────────────────────────────────────────────────

export type BrandReviewStatus = "open" | "approved" | "changes_requested" | "withdrawn" | "closed";
/** Was die Marke sieht: dazu „abgelaufen“ (Frist vorbei) */
export type BrandReviewState = BrandReviewStatus | "expired";

export const BRAND_REVIEW_STATE_LABEL: Readonly<Record<BrandReviewState, { de: string; en: string; tone: "neutral" | "info" | "warning" | "success" | "error" }>> = {
  open: { de: "Bei der Marke", en: "With the brand", tone: "info" },
  approved: { de: "Von der Marke freigegeben", en: "Approved by the brand", tone: "success" },
  changes_requested: { de: "Marke wünscht Änderungen", en: "Brand requested changes", tone: "warning" },
  withdrawn: { de: "Link zurückgezogen", en: "Link withdrawn", tone: "neutral" },
  closed: { de: "Vom Team entschieden", en: "Decided by the agency", tone: "neutral" },
  expired: { de: "Link abgelaufen", en: "Link expired", tone: "error" },
};

/** Stand eines Links aus Sicht des Teams: offen, aber Frist vorbei = abgelaufen. */
export function brandReviewState(r: { status: string; expires_at: string }, now: Date = new Date()): BrandReviewState {
  if (r.status === "open" && new Date(r.expires_at).getTime() < now.getTime()) return "expired";
  return (["open", "approved", "changes_requested", "withdrawn", "closed"] as const).includes(r.status as BrandReviewStatus) ? (r.status as BrandReviewStatus) : "closed";
}

export interface ReviewRounds {
  used: number;
  /** null: die Agentur verbirgt die Anzahl (dann kommt nur next_extra = true, wenn die nächste Änderung zusätzlich ist) */
  included: number | null;
  next_extra: boolean;
}

/**
 * Hinweis für die Marke vor „Änderung wünschen“: wie viele Korrekturschleifen enthalten sind und ob die nächste darüber
 * liegt. Ohne Betrag (Konditionen sieht nur das Team). Verbirgt die Agentur die Anzahl, steht nur der Satz zur gesonderten
 * Berechnung da, wenn die nächste Änderung zusätzlich ist (die Marke bestätigt ihn trotzdem).
 */
export function roundsNote(r: ReviewRounds | null | undefined, lang: Language): string | null {
  if (!r) return null;
  if (r.included == null) {
    if (!r.next_extra) return null;
    return lang === "en"
      ? "Another change request is an additional revision round that is charged separately as agreed."
      : "Ein weiterer Änderungswunsch ist eine zusätzliche Korrekturschleife, die laut Vereinbarung gesondert berechnet wird.";
  }
  const n = r.used + 1;
  if (lang === "en") {
    const inc = r.included === 1 ? "1 revision round is" : `${r.included} revision rounds are`;
    return r.next_extra
      ? `${inc} included and ${r.used === 1 ? "has" : "have"} been used. Another change request is an additional revision round that is charged separately as agreed.`
      : `A change request would be revision round ${n} of ${r.included} included.`;
  }
  const inc = r.included === 1 ? "1 Korrekturschleife ist" : `${r.included} Korrekturschleifen sind`;
  return r.next_extra
    ? `${inc} enthalten und genutzt. Ein weiterer Änderungswunsch ist eine zusätzliche Korrekturschleife, die laut Vereinbarung gesondert berechnet wird.`
    : `Ein Änderungswunsch wäre Korrekturschleife ${n} von ${r.included} enthaltenen.`;
}

/** Fehlerhinweise der Links (hint aus der Datenbank) als Titel und Text, zweisprachig. */
export function linkProblem(hint: string | null | undefined, lang: Language): { title: string; text: string } {
  const de: Record<string, [string, string]> = {
    "link:invalid": ["Dieser Link ist ungültig.", "Bitte öffnen Sie den Link direkt aus der E-Mail. Vielleicht wurde er unvollständig kopiert."],
    "link:expired": ["Dieser Link ist abgelaufen.", "Bitte fordern Sie bei Ihrer Kontaktperson einen neuen Link an."],
    "link:withdrawn": ["Dieser Link ist nicht mehr gültig.", "Die Agentur hat ihn zurückgezogen oder durch einen neuen ersetzt. Bitte verwenden Sie den Link aus der neuesten E-Mail."],
    "org:read_only": ["Derzeit nicht verfügbar.", "Bitte wenden Sie sich an Ihre Kontaktperson bei der Agentur."],
    // 0425 (S-7): Vertrag der Agentur beendet, neutral ohne Daten der Agentur
    "link:ended": ["Dieser Link ist nicht mehr verfügbar.", "Bitte wenden Sie sich an Ihre Kontaktperson."],
  };
  const en: Record<string, [string, string]> = {
    "link:invalid": ["This link is invalid.", "Please open the link directly from the email. It may have been copied incompletely."],
    "link:expired": ["This link has expired.", "Please ask your contact person for a new link."],
    "link:withdrawn": ["This link is no longer valid.", "The agency withdrew it or replaced it with a new one. Please use the link from the latest email."],
    "org:read_only": ["Currently unavailable.", "Please contact your contact person at the agency."],
    "link:ended": ["This link is no longer available.", "Please contact your contact person."],
  };
  const t = (lang === "en" ? en : de)[hint ?? ""] ?? (lang === "en"
    ? ["The page could not be loaded.", "Please try again in a moment."] as [string, string]
    : ["Die Seite ließ sich nicht laden.", "Bitte versuchen Sie es in einem Moment erneut."] as [string, string]);
  return { title: t[0], text: t[1] };
}

/** Fehler beim Entscheiden (hint aus brand_review_decide) in der Sprache der Seite; null für Unbekanntes. */
export function reviewProblem(hint: string | null | undefined, lang: Language): string | null {
  const de: Record<string, string> = {
    "review:decided": "Zu dieser Fassung liegt schon eine Entscheidung vor.",
    "review:closed": "Die Agentur hat diese Fassung bereits abschließend bearbeitet.",
    "review:extra_round": "Diese Änderung ist eine zusätzliche Korrekturschleife. Bitte bestätigen Sie, dass sie gesondert berechnet wird.",
    "review:name": "Bitte geben Sie Ihren Namen an.",
    "review:comment": "Bitte beschreiben Sie die gewünschte Änderung (höchstens 4000 Zeichen).",
    "review:email": "Bitte geben Sie eine gültige E-Mail-Adresse an.",
  };
  const en: Record<string, string> = {
    "review:decided": "A decision on this version has already been made.",
    "review:closed": "The agency has already completed this version.",
    "review:extra_round": "This change is an additional revision round. Please confirm that it is charged separately.",
    "review:name": "Please enter your name.",
    "review:comment": "Please describe the change you would like (at most 4000 characters).",
    "review:email": "Please enter a valid email address.",
  };
  return (lang === "en" ? en : de)[hint ?? ""] ?? null;
}

// ── Kennzahlen ────────────────────────────────────────────────────────────────────────────────────────────────────

export const METRIC_KEYS = ["reach", "impressions", "views", "interactions", "link_clicks"] as const;
export type MetricKey = (typeof METRIC_KEYS)[number];

export const METRIC_LABEL: Readonly<Record<MetricKey, { de: string; en: string; hint_de: string; hint_en: string }>> = {
  reach: { de: "Reichweite", en: "Reach", hint_de: "Erreichte Konten", hint_en: "Accounts reached" },
  impressions: { de: "Impressionen", en: "Impressions", hint_de: "Wie oft der Beitrag angezeigt wurde", hint_en: "How often the content was shown" },
  views: { de: "Aufrufe", en: "Views", hint_de: "Video-Aufrufe (TikTok, YouTube, Reels)", hint_en: "Video views (TikTok, YouTube, Reels)" },
  interactions: { de: "Interaktionen", en: "Interactions", hint_de: "Likes, Kommentare, Speichern, Teilen", hint_en: "Likes, comments, saves, shares" },
  link_clicks: { de: "Link-Klicks", en: "Link clicks", hint_de: "Klicks auf Link oder Sticker", hint_en: "Clicks on link or sticker" },
};

/**
 * Zahl aus einer Eingabe: „12300“, „12.300“, „12,300“, „12 300“, „12'300“ oder „12’300“ (typografischer Apostroph, so
 * schreiben iOS und Intl de-CH); leer = null; sonst NaN (Fehler).
 */
export function parseCount(text: string): number | null {
  const t = text.trim().replace(/[\u00a0\u202f]/g, " ").replace(/\u2019/g, "'");
  if (!t) return null;
  if (/^\d+$/.test(t)) return Number(t);
  if (/^\d{1,3}([.,' ]\d{3})+$/.test(t)) return Number(t.replace(/[.,' ]/g, ""));
  // Prüf-Chat CP-018 (02.11.2026): Schreibweise wie in Instagram („1,2k“, „1.2K“, „3,4 Mio.“, „12 Tsd.“) umrechnen
  const kurz = /^(\d+(?:[.,]\d+)?)\s*(k|tsd\.?|tausend|m|mio\.?|mill?\.?|million(?:en)?)$/i.exec(t);
  if (kurz) {
    const faktor = /^(k|tsd|tausend)/i.test(kurz[2]!) ? 1_000 : 1_000_000;
    return Math.round(Number(kurz[1]!.replace(",", ".")) * faktor);
  }
  return Number.NaN;
}

/**
 * Schreibweise der Zahlen: Englisch en-GB, Deutsch nach der Locale der Agentur (org_settings.locale): de-CH mit Apostroph
 * als Tausendertrenner und Punkt als Dezimalzeichen (wie formatMoney), de-AT, sonst de-DE.
 */
export function numberLocale(lang: Language, locale?: string | null): "en-GB" | "de-DE" | "de-AT" | "de-CH" {
  if (lang === "en") return "en-GB";
  if (locale === "de-CH" || locale === "de-LI") return "de-CH";
  if (locale === "de-AT") return "de-AT";
  return "de-DE";
}

function formatNumber(n: number, lang: Language, locale: string | null | undefined, digits: number): string {
  const loc = numberLocale(lang, locale);
  const s = new Intl.NumberFormat(loc, { maximumFractionDigits: digits }).format(n).replace(/\u202f/g, "\u00a0");
  return loc === "de-CH" ? s.replace(/\u2019/g, "'") : s;
}

/** „12.300“ (de-DE), „12 300“ (de-AT), „12'300“ (de-CH) bzw. „12,300“ (en). */
export function formatCount(n: number, lang: Language, locale?: string | null): string {
  return formatNumber(n, lang, locale, 0);
}

/** Interaktionsrate: „11,4 %“ (de-DE/de-AT), „11.4 %“ (de-CH), „11.4 %“ (en). */
export function formatRate(n: number, lang: Language, locale?: string | null): string {
  return `${formatNumber(n, lang, locale, 1)} %`;
}

/** Ab wann der Creator Kennzahlen hochladen soll: Veröffentlichung plus Tage (Stories eigener Wert); 0 Tage = keine Aufgabe. */
export function metricsDueOn(publishedOn: string | null | undefined, format: string, days: { standard: number; story: number }): string | null {
  if (!publishedOn) return null;
  const d = format === "story" || format === "story_set" ? days.story : days.standard;
  return d > 0 ? addDaysIso(publishedOn.slice(0, 10), d) : null;
}

// ── Bericht ───────────────────────────────────────────────────────────────────────────────────────────────────────

export interface ReportManualMetrics {
  reach?: number;
  impressions?: number;
  interactions?: number;
  link_clicks?: number;
  views?: number;
  captured_on?: string;
  source?: "creator" | "team";
}
export interface ReportInstagramMetrics {
  reach?: number;
  views?: number;
  likes?: number;
  comments?: number;
  saved?: number;
  shares?: number;
  interactions?: number;
  fetched_at?: string;
}
export interface ReportFile {
  id: string;
  name: string;
  mime: string;
  size: number;
}
export interface ReportItem {
  deliverable_id: string;
  label: string;
  format: string;
  platform: string;
  creator: string;
  published_on: string | null;
  live_url: string | null;
  metrics: ReportManualMetrics | null;
  instagram: ReportInstagramMetrics | null;
  files: ReportFile[];
}
export interface BrandReport {
  report_id: string;
  title: string;
  intro: string | null;
  language: Language;
  expires_at: string;
  created_at: string;
  generated_at: string;
  agency: { name: string; address: string | null; email: string | null; website: string | null; time_zone: string | null; locale: string | null;
    /** Logo der Agentur (Bucket branding, 0122): Dokumentkopf von Seite und PDF */
    logo_path?: string | null };
  brand: string | null;
  scope: { kind: "deal" | "campaign"; title: string | null };
  items: ReportItem[];
  open_count: number;
  instagram: boolean;
  team_view?: boolean;
  revoked_at?: string | null;
}

const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && /^\d+(\.\d+)?$/.test(v) ? Number(v) : undefined);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

/** Antwort der Datenbank in den Typ bringen; unbekannte Felder fallen weg, „kein Wert“ bleibt weg (nie 0). */
export function parseBrandReport(raw: unknown): BrandReport {
  const o = (typeof raw === "string" ? JSON.parse(raw) : raw) as Record<string, unknown>;
  const ag = (o.agency ?? {}) as Record<string, unknown>;
  const sc = (o.scope ?? {}) as Record<string, unknown>;
  const pick = <K extends string>(src: unknown, keys: readonly K[]) => {
    if (!src || typeof src !== "object") return null;
    const out: Record<string, unknown> = {};
    for (const k of keys) {
      const v = (src as Record<string, unknown>)[k];
      const n = num(v);
      if (n !== undefined) out[k] = n;
      else if (typeof v === "string" && (k === "captured_on" || k === "fetched_at" || k === "source")) out[k] = v;
    }
    return out;
  };
  const items = (Array.isArray(o.items) ? o.items : []).map((x) => {
    const i = x as Record<string, unknown>;
    const m = pick(i.metrics, ["reach", "impressions", "interactions", "link_clicks", "views", "captured_on", "source"] as const);
    const ig = pick(i.instagram, ["reach", "views", "likes", "comments", "saved", "shares", "interactions", "fetched_at"] as const);
    return {
      deliverable_id: String(i.deliverable_id ?? ""), label: String(i.label ?? ""), format: String(i.format ?? ""), platform: String(i.platform ?? ""),
      creator: String(i.creator ?? ""), published_on: str(i.published_on)?.slice(0, 10) ?? null, live_url: str(i.live_url),
      metrics: m && Object.keys(m).some((k) => METRIC_KEYS.includes(k as MetricKey)) ? (m as ReportManualMetrics) : null,
      instagram: ig && Object.keys(ig).some((k) => k !== "fetched_at") ? (ig as ReportInstagramMetrics) : null,
      files: (Array.isArray(i.files) ? i.files : []).map((f) => {
        const r = f as Record<string, unknown>;
        return { id: String(r.id ?? ""), name: String(r.name ?? ""), mime: String(r.mime ?? ""), size: num(r.size) ?? 0 };
      }),
    } satisfies ReportItem;
  });
  return {
    report_id: String(o.report_id ?? ""), title: String(o.title ?? ""), intro: str(o.intro), language: o.language === "en" ? "en" : "de",
    expires_at: String(o.expires_at ?? ""), created_at: String(o.created_at ?? ""), generated_at: String(o.generated_at ?? o.created_at ?? ""),
    agency: { name: String(ag.name ?? ""), address: str(ag.address), email: str(ag.email), website: str(ag.website), time_zone: str(ag.time_zone), locale: str(ag.locale), logo_path: str(ag.logo_path) },
    brand: str(o.brand), scope: { kind: sc.kind === "campaign" ? "campaign" : "deal", title: str(sc.title) },
    items, open_count: num(o.open_count) ?? 0, instagram: o.instagram === true,
    ...(o.team_view === true ? { team_view: true } : {}), ...(o.revoked_at !== undefined ? { revoked_at: str(o.revoked_at) } : {}),
  };
}

export interface ItemValue {
  value: number;
  source: "manual" | "instagram";
}

/** Wert eines Beitrags: von Hand zuerst (Creator oder Team), sonst Instagram (Aufrufe statt Impressionen, seit Meta sie so nennt). */
export function itemValue(item: Pick<ReportItem, "metrics" | "instagram">, key: MetricKey): ItemValue | null {
  const m = item.metrics?.[key];
  if (typeof m === "number") return { value: m, source: "manual" };
  const ig = item.instagram;
  if (!ig) return null;
  const v = key === "reach" ? ig.reach : key === "views" ? ig.views : key === "interactions" ? ig.interactions : undefined;
  return typeof v === "number" ? { value: v, source: "instagram" } : null;
}

export interface ReportTotals {
  /** Summe je Kennzahl über die Beiträge mit Wert; null, wenn kein Beitrag einen Wert hat */
  sums: Record<MetricKey, number | null>;
  /** Anzahl Beiträge mit Wert je Kennzahl */
  counts: Record<MetricKey, number>;
  live: number;
  /** Interaktionsrate: Interaktionen / Reichweite über die Beiträge, die beides haben (in Prozent, eine Nachkommastelle) */
  engagementRate: number | null;
}

export function reportTotals(items: readonly Pick<ReportItem, "metrics" | "instagram">[]): ReportTotals {
  const sums = Object.fromEntries(METRIC_KEYS.map((k) => [k, null])) as Record<MetricKey, number | null>;
  const counts = Object.fromEntries(METRIC_KEYS.map((k) => [k, 0])) as Record<MetricKey, number>;
  let er = 0, rr = 0;
  for (const it of items) {
    for (const k of METRIC_KEYS) {
      const v = itemValue(it, k);
      if (!v) continue;
      sums[k] = (sums[k] ?? 0) + v.value;
      counts[k] += 1;
    }
    const i = itemValue(it, "interactions"), r = itemValue(it, "reach");
    if (i && r && r.value > 0) { er += i.value; rr += r.value; }
  }
  return { sums, counts, live: items.length, engagementRate: rr > 0 ? Math.round((er / rr) * 1000) / 10 : null };
}

export const REPORT_LABELS = {
  de: {
    report: "Bericht", forBrand: (b: string) => `für ${b}`, by: (a: string) => `erstellt von ${a}`, validUntil: "Link gültig bis", generated: "Stand",
    overview: "Überblick", published: "Veröffentlichte Beiträge", tileLive: "Beiträge live", open: (n: number) => `${n} ${n === 1 ? "Beitrag ist" : "Beiträge sind"} noch nicht veröffentlicht.`,
    item: "Beitrag", creator: "Creator", date: "Veröffentlicht", link: "Link", values: "Kennzahlen", proof: "Nachweise",
    manual: (d: string | null, who: string | undefined) => `von Hand eingetragen${who === "team" ? " (Agentur)" : who === "creator" ? " (Creator)" : ""}${d ? `, Stand ${d}` : ""}`,
    instagram: "aus Instagram (mit Einwilligung des Creators)", none: "Noch keine Kennzahlen.", er: "Interaktionsrate",
    erNote: "Interaktionen geteilt durch Reichweite über die Beiträge mit beiden Werten.",
    sumNote: "Summen über die Beiträge mit Wert. Reichweiten verschiedener Beiträge können dieselben Konten mehrfach zählen.",
    noItems: "Noch kein Beitrag veröffentlicht.", screenshots: (n: number) => `${n} ${n === 1 ? "Screenshot" : "Screenshots"} (im Link abrufbar)`,
    screenshotsAttached: (n: number) => `${n} ${n === 1 ? "Nachweis" : "Nachweise"} im Anhang dieses PDF`,
    appendix: "Nachweise", appendixIntro: "Screenshots der Statistik je Beitrag, wie sie hochgeladen wurden. Der Prüfwert (SHA-256) gehört zur hochgeladenen Datei.",
    notEmbedded: "Als Datei im Link abrufbar (hier nicht eingebettet).",
    page: (a: number, b: number) => `Seite ${a} von ${b}`, table: "Kennzahlen im Überblick", colItem: "Beitrag", colDate: "Datum", colReach: "Reichweite · Impressionen", colMore: "Aufrufe · Interaktionen · Link-Klicks",
  },
  en: {
    report: "Report", forBrand: (b: string) => `for ${b}`, by: (a: string) => `prepared by ${a}`, validUntil: "Link valid until", generated: "As of",
    overview: "Overview", published: "Published content", tileLive: "Posts live", open: (n: number) => `${n} ${n === 1 ? "item is" : "items are"} not published yet.`,
    item: "Content", creator: "Creator", date: "Published", link: "Link", values: "Metrics", proof: "Proof",
    manual: (d: string | null, who: string | undefined) => `entered manually${who === "team" ? " (agency)" : who === "creator" ? " (creator)" : ""}${d ? `, as of ${d}` : ""}`,
    instagram: "from Instagram (with the creator's consent)", none: "No metrics yet.", er: "Engagement rate",
    erNote: "Interactions divided by reach across the content with both values.",
    sumNote: "Totals across the content with a value. Reach of different posts may count the same accounts more than once.",
    noItems: "No content published yet.", screenshots: (n: number) => `${n} ${n === 1 ? "screenshot" : "screenshots"} (available via the link)`,
    screenshotsAttached: (n: number) => `${n} ${n === 1 ? "proof" : "proofs"} in the appendix of this PDF`,
    appendix: "Proof", appendixIntro: "Screenshots of the insights per post, as uploaded. The checksum (SHA-256) belongs to the uploaded file.",
    notEmbedded: "Available as a file via the link (not embedded here).",
    page: (a: number, b: number) => `Page ${a} of ${b}`, table: "Metrics at a glance", colItem: "Content", colDate: "Date", colReach: "Reach · Impressions", colMore: "Views · Interactions · Link clicks",
  },
} as const;

export function reportDate(iso: string | null | undefined, lang: Language, timeZone?: string | null): string {
  if (!iso) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: /^\d{4}-\d{2}-\d{2}$/.test(iso) ? "UTC" : timeZone ?? "UTC" }).format(d);
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Nachweis für das PDF: Prüfwert der hochgeladenen Datei und, bei Bildern, das Bild als JPEG (die App wandelt PNG/WebP um). */
export interface ReportProof {
  sha256: string;
  image?: ContractPdfImage | null;
}

/**
 * Bericht als PDF (gleicher Satz wie Verträge): Kopf, Überblick mit Summen, je Beitrag die Kennzahlen, Nachweise im Anhang
 * (Screenshots eingebettet, dazu Dateiname und SHA-256; Stories sind nach 24 Stunden weg, das PDF behält den Nachweis auch nach
 * Ablauf des Links), Tabelle am Ende. `proofs` je Anhang-ID; ohne Eintrag steht die Datei nur als Hinweis im PDF.
 */
export function renderBrandReportPdf(r: BrandReport, opts: { now?: Date; proofs?: Readonly<Record<string, ReportProof>>; logo?: ContractPdfImage | null } = {}): Uint8Array {
  const lang = r.language;
  const L = REPORT_LABELS[lang];
  const loc = r.agency.locale;
  const proofs = opts.proofs ?? {};
  const f = (n: number | null | undefined) => (n == null ? "–" : formatCount(n, lang, loc));
  const tz = r.agency.time_zone;
  const t = reportTotals(r.items);
  const html: string[] = [];
  html.push(`<h1>${esc(r.title)}</h1>`);
  html.push(`<p>${esc([r.brand ? L.forBrand(r.brand) : null, L.by(r.agency.name), `${L.generated} ${reportDate(r.generated_at, lang, tz)}`].filter(Boolean).join(" · "))}</p>`);
  if (r.intro) html.push(`<p>${esc(r.intro).replace(/\n/g, "<br>")}</p>`);
  html.push(`<h2>${L.overview}</h2><ul>`);
  html.push(`<li><strong>${L.published}:</strong> ${r.items.length}</li>`);
  for (const k of METRIC_KEYS) if (t.sums[k] != null) html.push(`<li><strong>${METRIC_LABEL[k][lang]}:</strong> ${f(t.sums[k])} (${t.counts[k]}/${r.items.length})</li>`);
  if (t.engagementRate != null) html.push(`<li><strong>${L.er}:</strong> ${formatRate(t.engagementRate, lang, loc)}</li>`);
  html.push("</ul>");
  html.push(`<p>${esc(L.sumNote)}${t.engagementRate != null ? ` ${esc(L.erNote)}` : ""}</p>`);
  if (r.open_count > 0) html.push(`<p>${esc(L.open(r.open_count))}</p>`);
  if (!r.items.length) html.push(`<p>${esc(L.noItems)}</p>`);
  for (const it of r.items) {
    html.push(`<h2>${esc(it.label)}</h2>`);
    html.push(`<p>${esc([`${L.creator}: ${it.creator}`, it.published_on ? `${L.date}: ${reportDate(it.published_on, lang)}` : null].filter(Boolean).join(" · "))}${it.live_url ? `<br>${L.link}: ${esc(it.live_url)}` : ""}</p>`);
    const rows = METRIC_KEYS.map((k) => ({ k, v: itemValue(it, k) })).filter((x) => x.v);
    if (!rows.length) html.push(`<p>${esc(L.none)}</p>`);
    else {
      html.push("<ul>");
      for (const x of rows) html.push(`<li>${METRIC_LABEL[x.k][lang]}: <strong>${f(x.v!.value)}</strong>${x.v!.source === "instagram" ? " (Instagram)" : ""}</li>`);
      if (it.instagram) {
        const extra = (["likes", "comments", "saved", "shares"] as const).filter((k) => typeof it.instagram![k] === "number")
          .map((k) => `${lang === "en" ? { likes: "Likes", comments: "Comments", saved: "Saves", shares: "Shares" }[k] : { likes: "Likes", comments: "Kommentare", saved: "Gespeichert", shares: "Geteilt" }[k]} ${f(it.instagram![k])}`);
        if (extra.length) html.push(`<li>${esc(extra.join(" · "))}</li>`);
      }
      html.push("</ul>");
      const src = [it.metrics ? L.manual(it.metrics.captured_on ? reportDate(it.metrics.captured_on, lang) : null, it.metrics.source) : null, it.instagram ? L.instagram : null].filter(Boolean);
      html.push(`<p>${esc(src.join(" · "))}</p>`);
    }
    if (it.files.length) {
      const inPdf = it.files.filter((x) => proofs[x.id]).length;
      html.push(`<p>${esc(`${L.proof}: ${inPdf ? L.screenshotsAttached(inPdf) : L.screenshots(it.files.length)}`)}</p>`);
    }
  }
  const figures = r.items.flatMap((it) => it.files.filter((x) => proofs[x.id]).map((x) => {
    const pr = proofs[x.id]!;
    return {
      caption: `${it.label} · ${it.creator}: ${x.name}`,
      lines: [`SHA-256: ${pr.sha256}`, ...(pr.image ? [] : [L.notEmbedded])],
      image: pr.image ?? null,
    };
  }));
  return renderContractPdf({
    language: lang, title: r.title, header: [r.agency.name, r.brand, L.report].filter(Boolean).join(" · "), bodyHtml: html.join(""), signatures: [],
    logo: opts.logo ?? null,
    figures: figures.length ? { title: L.appendix, intro: L.appendixIntro, items: figures } : null,
    protocol: r.items.length ? {
      title: L.table, intro: `${L.generated} ${reportDate(r.generated_at, lang, tz)}`, hashes: [],
      columns: [L.colItem, L.colDate, L.colReach, L.colMore],
      rows: r.items.map((it) => ({
        at: `${it.label} · ${it.creator}`, event: reportDate(it.published_on, lang),
        person: `${f(itemValue(it, "reach")?.value)} · ${f(itemValue(it, "impressions")?.value)}`,
        evidence: `${f(itemValue(it, "views")?.value)} · ${f(itemValue(it, "interactions")?.value)} · ${f(itemValue(it, "link_clicks")?.value)}`,
      })),
    } : null,
    labels: { page: L.page },
  }, { now: opts.now ?? new Date() });
}
